// URL path-prefix routing helpers for i18n.
//
// Decisión (ver docs/I18N_PLAN.md §5):
//   - Galego ("gl") ocupa as rutas raíz: /buscar, /anuncio/:id, ...
//   - Español ("es") móntase baixo o prefixo /es: /es/buscar, ...
//   - Outros idiomas (pt, en) aínda non teñen URL prefix (non avaliables).
//
// Implementación: route tree duplicado en App.jsx baixo `/es/*`, con
// `<LocaleHandler lang="es">` que sincroniza i18next ao montarse. Esta
// abordaxe é menos elegante que un :locale? param, pero non require tocar
// path de cada Route fillo nin de cada Link na app.
//
// Para Links internos: usa o hook `useLocalizedHref(path)` que devolve
// o path co prefixo correcto en función do idioma actual. Para
// re-prefixar a URL ao cambiar idioma manualmente (selector header/footer),
// usa `switchLanguagePath(currentPathname, fromLang, toLang)`.

export const LOCALE_PREFIXES = {
  gl: "",        // raíz
  es: "/es",
};

export const PREFIXED_LOCALES = ["es"]; // idiomas que teñen prefixo de URL

/**
 * Detecta o locale dunha URL pathname.
 * Devolve { locale, rest } onde rest é o pathname sen o prefixo.
 *
 * Exemplos:
 *   "/es/buscar"  → { locale: "es", rest: "/buscar" }
 *   "/buscar"     → { locale: "gl", rest: "/buscar" }
 *   "/es"         → { locale: "es", rest: "/" }
 */
export function parseLocaleFromPath(pathname) {
  if (!pathname) return { locale: "gl", rest: "/" };
  for (const lang of PREFIXED_LOCALES) {
    const prefix = LOCALE_PREFIXES[lang];
    if (pathname === prefix) return { locale: lang, rest: "/" };
    if (pathname.startsWith(prefix + "/")) {
      return { locale: lang, rest: pathname.slice(prefix.length) || "/" };
    }
  }
  return { locale: "gl", rest: pathname };
}

/**
 * Aplica o prefixo do locale dado a un path "raíz" (galego).
 *
 * Exemplos:
 *   localizedPath("/buscar", "es") → "/es/buscar"
 *   localizedPath("/buscar", "gl") → "/buscar"
 *   localizedPath("/", "es")        → "/es"
 */
export function localizedPath(path, locale) {
  const lang = (locale || "gl").split("-")[0];
  const prefix = LOCALE_PREFIXES[lang] ?? "";
  if (!prefix) return path;
  if (path === "/" || path === "") return prefix || "/";
  if (path.startsWith(prefix + "/") || path === prefix) return path;
  return prefix + (path.startsWith("/") ? path : "/" + path);
}

/**
 * Re-escribe a URL actual ao cambiar de idioma.
 * Mantén o resto do path, querystring e hash.
 *
 * Ex.: switchLanguagePath("/buscar?x=1", "gl", "es") → "/es/buscar?x=1"
 */
export function switchLanguagePath(fullUrl, toLang) {
  // fullUrl pode ser o pathname ou pathname+search+hash.
  const [pathOnly, ...rest] = fullUrl.split(/(\?|#)/);
  const tail = rest.join("");
  const { rest: bare } = parseLocaleFromPath(pathOnly);
  const newPath = localizedPath(bare, toLang);
  return newPath + tail;
}
